import { useRouter } from 'next/router';
import { RefObject, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { loginStatusState } from '@recoil/auth';
import { useSetRecoilState } from 'recoil';
import { useQueryClient } from 'react-query';

// hooks
import { useUserSignOutFetch } from '@hooks/fetch/useUserFetch';

// components
import Typography from '@components/_atoms/Typography';
import Button from '@components/_atoms/Button';
import Modal from '@components/_atoms/Modal';
import TextInput from '@components/_atoms/TextInput';

// constants
import { ButtonColor, TypoVariant } from '@constants/atoms';
import { gray } from '@styles/Colors';
import { SIGNOUT_COMPLETE } from '@constants/routes/routes';
import { useTranslation } from 'next-i18next';
import { removeCookie } from '@utils/cookie';
import { ACCESS_TOKEN } from '@utils/cookie';
import { REFRESH_TOKEN } from '@utils/cookie';
import ButtonCode from '@constants/log/buttonCode';

type Props = {
  openModal: boolean;
  modalRef: RefObject<HTMLElement>;
  handleModalClose: () => void;
};

const FormWrapper = styled.form`
  display: grid;
  row-gap: 16px;
`;

export default function SignOutModal({ openModal, modalRef, handleModalClose }: Props) {
  const { t } = useTranslation('user');
  const router = useRouter();
  const queryClient = useQueryClient();
  const currentDomain = window.location.hostname;

  const setloginStatus = useSetRecoilState(loginStatusState);
  const [text, setText] = useState('');

  const { userSignOutFetchMutate } = useUserSignOutFetch();

  // form change
  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setText(value);
  };

  // 모달 open 시 text 초기화
  useEffect(() => {
    setText('');
  }, [openModal]);

  // 회원탈퇴 submit
  const handleFormSubmit = () => {
    userSignOutFetchMutate(void 0, {
      onSuccess: () => {
        setloginStatus(false);
        queryClient.invalidateQueries(['user']);
        removeCookie(ACCESS_TOKEN, { domain: currentDomain, path: '/' });
        removeCookie(REFRESH_TOKEN, { domain: currentDomain, path: '/' });

        router.replace(SIGNOUT_COMPLETE);
      },
    });
  };

  return (
    <Modal
      width="400px"
      modalRef={modalRef}
      open={openModal}
      title={t('signout.modal-title')}
      handleModalClose={handleModalClose}>
      <FormWrapper
        onSubmit={(e) => {
          e.preventDefault();
          handleFormSubmit();
        }}>
        <Typography variant={TypoVariant.B4} color={gray.gray7}>
          {t('signout.modal-input')}
        </Typography>
        <TextInput placeholder={t('signout.modal-placeholder')} value={text} onChange={handleFormChange} />
        <Button
          disabled={!text || text !== t('signout.modal-placeholder')}
          width="100%"
          color={ButtonColor.OutlinedRed}
          text={t('signout.modal-button')}
          steelbosoId={ButtonCode.BTN_SIGNOUT}
        />
      </FormWrapper>
    </Modal>
  );
}
